import { RestaurantAPI } from './RestaurantAPI.js';
import { NotificationManager } from '../utils/notifications.js';
import { themeManager } from './ThemeManager.js';
import { categories, menuItems } from '../data/menuData.js';
import deviceManager from './DeviceManager.js';
import { config } from '../utils/config.js';

export class ConfigurationManager {
  constructor() {
    this.api = new RestaurantAPI();
    this.currentConfig = null;
    this.currentRestaurantId = this.getInitialRestaurantId();
    this.isInitialized = false;
    this.configChangeListeners = [];
    this.themeManager = themeManager;
    this.deviceManager = deviceManager;
    this.deviceConfig = null;

    this.setupDeviceEventListeners();
  }

  /**
   * Get initial restaurant ID from URL parameters, headers, or environment
   * @returns {string|null} Restaurant ID or null if not found in production
   */
  getInitialRestaurantId() {
    console.log('[ConfigurationManager] Looking for restaurant ID...');

    const hash = window.location.hash;
    console.log('[ConfigurationManager] URL hash:', hash);
    const menuMatch = hash.match(/#menu\/([^/?]+)/);
    if (menuMatch) {
      console.log('[ConfigurationManager] Found restaurant ID in hash:', menuMatch[1]);
      return menuMatch[1];
    }

    const urlParams = new URLSearchParams(window.location.search);
    const restaurantFromUrl = urlParams.get('restaurantId') || urlParams.get('restaurant');
    console.log('[ConfigurationManager] URL params restaurant:', restaurantFromUrl);

    if (restaurantFromUrl) {
      return restaurantFromUrl;
    }

    if (!config.isDev()) {
      console.log('[ConfigurationManager] Production mode, no restaurant ID found');
      return null;
    }

    const defaultId = config.getDefaultRestaurantId();
    console.log('[ConfigurationManager] Using default restaurant ID:', defaultId);
    return defaultId;
  }

  /**
   * Check if a valid restaurant ID is available
   * @returns {boolean} True if restaurant ID is available
   */
  hasValidRestaurantId() {
    const restaurantId = this.getInitialRestaurantId();
    return restaurantId !== null && restaurantId !== '' && restaurantId !== 'undefined';
  }

  /**
   * Initialize configuration manager
   * @returns {Promise<Object>} Initial configuration
   */
  async initialize() {
    try {
      if (this.deviceManager.isAndroid()) {
        await this.initializeDeviceConfiguration();
      }

      this.currentConfig = await this.api.getRestaurantConfig(this.currentRestaurantId);
      if (this.currentConfig?.id && this.currentRestaurantId !== this.currentConfig.id) {
        console.warn('[ConfigurationManager] Overriding selected restaurant to device restaurant', {
          selected: this.currentRestaurantId,
          device: this.currentConfig.id
        });
        this.currentRestaurantId = this.currentConfig.id;
      }
      const menuData = await this.api.getMenuData(this.currentRestaurantId);

      this.currentConfig.menu = menuData;

      if (this.deviceConfig) {
        this.applyDeviceConfiguration();
      }

      this.applyBranding();

      this.isInitialized = true;
      this.notifyConfigChange();
      return this.currentConfig;
    } catch (error) {
      console.error('Failed to initialize configuration:', error);

      this.currentConfig = this.getFallbackConfiguration();

      this.applyBranding();

      this.isInitialized = true;
      this.notifyConfigChange();

      NotificationManager.warning('Running in offline mode with default configuration');
      return this.currentConfig;
    }
  }

  /**
   * Get fallback configuration when API is unavailable
   * @returns {Object} Fallback configuration
   */
  getFallbackConfiguration() {
    return {
      id: 'fallback-restaurant',
      name: 'Oliveto',
      tagline: 'Experience the rich flavors of modern Italian cuisine, crafted with passion and tradition.',
      subtext: 'Please tap to start ordering',
      branding: {
        primaryColor: '#3E885B',
        secondaryColor: '#F9C846',
        logo: null,
        logoSettings: {
          width: 500,
          height: 498,
          alt: 'Oliveto Restaurant'
        },
        backgroundImage: '',
        backgroundImageOpacity: 100
      },
      kioskDisplaySettings: {
        welcomeScreen: {
          logoSettings: {
            url: null,
            width: 500,
            height: 498,
            alt: 'Oliveto Restaurant'
          }
        },
        colorPalette: {
          primary: '#3E885B',
          secondary: '#F9C846',
          accent: '#FF6B6B',
          error: '#F44336',
          warning: '#FF9800',
          success: '#4CAF50',
          info: '#2196F3'
        }
      },
      menu: {
        categories: categories,
        items: menuItems,
        specials: [
          {
            name: "Risotto al Tartufo Nero",
            price: 24.95,
            currency: "$",
            description: "Creamy arborio rice with seasonal wild mushrooms and freshly shaved black truffle"
          },
          {
            name: "Branzino alla Griglia",
            price: 32.95,
            currency: "$",
            description: "Grilled Mediterranean sea bass with lemon herb sauce and seasonal vegetables"
          }
        ]
      },
      settings: {
        currency: 'PLN',
        language: 'en',
        timezone: 'Europe/Warsaw'
      },
      contact: {
        phone: '+48 123 456 789',
        email: 'info@oliveto.com',
        address: 'Warsaw, Poland'
      }
    };
  }

  /**
   * Switch to a different restaurant
   * @param {string} restaurantId - New restaurant ID
   * @returns {Promise<Object>} New configuration
   */
  async switchRestaurant(restaurantId) {
    if (restaurantId === this.currentRestaurantId) {
      return this.currentConfig;
    }

    try {
      NotificationManager.info(`Switching to ${restaurantId}...`);

      const newConfig = await this.api.getRestaurantConfig(restaurantId);
      const menuData = await this.api.getMenuData(restaurantId);

      newConfig.menu = menuData;

      this.currentRestaurantId = restaurantId;
      this.currentConfig = newConfig;

      this.themeManager.loadFromBackend(newConfig);

      if (this.api.isDevEnvironment()) {
        localStorage.setItem('dev_selected_restaurant_id', restaurantId);
      }

      this.notifyConfigChange();
      NotificationManager.success(`Switched to ${newConfig.name}`);

      return newConfig;
    } catch (error) {
      console.error('Failed to switch restaurant:', error);
      NotificationManager.error(`Failed to switch to ${restaurantId}`);
      throw error;
    }
  }

  /**
   * Get current restaurant configuration
   * @returns {Object|null} Current configuration
   */
  getCurrentConfig() {
    return this.currentConfig;
  }

  /**
   * Get current restaurant ID
   * @returns {string} Current restaurant ID
   */
  getCurrentRestaurantId() {
    return this.currentRestaurantId;
  }

  /**
   * Check if running in development mode
   * @returns {boolean} Is development mode
   */
  isDevMode() {
    return this.api.isDevEnvironment();
  }

  /**
   * Get available restaurant options for dev mode
   * @returns {Promise<Array>} Restaurant options
   */
  async getDevRestaurantOptions() {
    if (this.api.isAndroidDevice) {
      return [{
        id: this.currentRestaurantId,
        name: 'Enrolled Restaurant',
        isEnrolled: true
      }];
    }

    return await this.api.getClientRestaurants();
  }

  /**
   * Reload current restaurant configuration
   * @returns {Promise<Object>} Reloaded configuration
   */
  async reloadConfig() {
    try {
      const newConfig = await this.api.getRestaurantConfig(this.currentRestaurantId);
      const menuData = await this.api.getMenuData(this.currentRestaurantId);

      newConfig.menu = menuData;

      this.currentConfig = newConfig;
      this.notifyConfigChange();
      NotificationManager.success('Configuration reloaded');
      return newConfig;
    } catch (error) {
      console.error('Failed to reload configuration:', error);
      NotificationManager.error('Failed to reload configuration');
      throw error;
    }
  }

  /**
   * Add a listener for configuration changes
   * @param {Function} listener - Function to call when config changes
   */
  addConfigChangeListener(listener) {
    this.configChangeListeners.push(listener);
  }

  setupDeviceEventListeners() {
    this.deviceManager.addListener((event, data) => {

      switch (event) {
        case 'deviceReady':
          this.handleDeviceReady(data);
          break;
        case 'deviceInfoLoaded':
          this.handleDeviceInfoLoaded(data);
          break;
      }
    });
  }

  async initializeDeviceConfiguration() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const restaurantId = urlParams.get('restaurantId');

      if (restaurantId) {
        this.currentRestaurantId = restaurantId;
      }

      const deviceInfo = this.deviceManager.getDeviceInfo();
      if (deviceInfo) {
        this.deviceConfig = deviceInfo;
      }

      this.deviceManager.logEvent('config_init', {
        deviceId: this.deviceManager.getDeviceId(),
        restaurantId: this.currentRestaurantId
      });
    } catch (error) {
      console.warn('Failed to initialize device configuration:', error);
    }
  }

  applyDeviceConfiguration() {
    if (!this.deviceConfig) return;

    try {
      if (this.deviceConfig.branding && this.currentConfig) {
        this.currentConfig.deviceBranding = this.deviceConfig.branding;
      }

    } catch (error) {
      console.warn('Failed to apply device configuration:', error);
    }
  }

  applyBranding() {
    if (this.deviceManager.isAndroid()) {
      const deviceBranding = this.deviceManager.getRestaurantBranding();
      if (deviceBranding) {
        this.themeManager.loadFromDeviceBranding(deviceBranding);
        return;
      }
    }

    this.themeManager.loadFromBackend(this.currentConfig);
  }

  handleDeviceReady(data) {

    if (data.deviceId) {
      this.deviceManager.logEvent('config_device_ready', {
        deviceId: data.deviceId,
        restaurantId: this.currentRestaurantId
      });
    }
  }

  handleDeviceInfoLoaded(data) {

    this.deviceConfig = data;
    this.applyBranding();
    this.notifyConfigChange();
  }

  getDeviceContext() {
    if (!this.deviceManager.isAndroid()) {
      return null;
    }

    return {
      deviceId: this.deviceManager.getDeviceId(),
      isAndroid: true,
      deviceInfo: this.deviceConfig,
      branding: this.deviceManager.getRestaurantBranding()
    };
  }

  /**
   * Remove a configuration change listener
   * @param {Function} listener - Function to remove
   */
  removeConfigChangeListener(listener) {
    const index = this.configChangeListeners.indexOf(listener);
    if (index > -1) {
      this.configChangeListeners.splice(index, 1);
    }
  }

  /**
   * Notify all listeners of configuration change
   */
  notifyConfigChange() {
    this.configChangeListeners.forEach(listener => {
      try {
        listener(this.currentConfig, this.currentRestaurantId);
      } catch (error) {
        console.error('Error in config change listener:', error);
      }
    });
  }

  /**
   * Get restaurant-specific branding
   * @returns {Object} Branding configuration
   */
  getBranding() {
    return this.currentConfig?.branding || {
      primaryColor: 'rgb(62, 136, 91)',
      secondaryColor: 'rgb(249, 200, 70)',
      logo: null,
      backgroundImage: null
    };
  }

  /**
   * Get restaurant menu data
   * @returns {Object} Menu configuration
   */
  getMenuData() {
    return this.currentConfig?.menu || {
      categories: [],
      items: {},
      specials: []
    };
  }

  /**
   * Get restaurant contact information
   * @returns {Object} Contact information
   */
  getContactInfo() {
    return this.currentConfig?.contact || {
      phone: null,
      email: null,
      address: null
    };
  }

  /**
   * Get restaurant settings
   * @returns {Object} Restaurant settings
   */
  getSettings() {
    return this.currentConfig?.settings || {
      currency: 'PLN',
      language: 'en',
      timezone: 'Europe/Rome'
    };
  }

  /**
   * Check if configuration is initialized
   * @returns {boolean} Is initialized
   */
  isConfigInitialized() {
    return this.isInitialized;
  }

  /**
   * Get saved restaurant selection from localStorage (dev mode only)
   * @returns {string|null} Saved restaurant ID
   */
  getSavedRestaurantSelection() {
    if (!this.api.isDevEnvironment()) return null;
    return localStorage.getItem('dev_selected_restaurant_id');
  }

  /**
   * Apply branding to page
   * @deprecated Use ThemeManager instead
   */
  applyBrandingLegacy() {

    if (this.currentConfig) {
      document.title = `${this.currentConfig.name || 'Restaurant'} - Tabbly Menu`;
    }
  }

  /**
   * Get current theme
   * @returns {Object} Current theme
   */
  getCurrentTheme() {
    return this.themeManager.getCurrentTheme();
  }

  /**
   * Generate contrast report for current theme
   * @returns {Object} Contrast report
   */
  getContrastReport() {
    return this.themeManager.generateContrastReport();
  }

}
